import * as React from "react";
import { CommonHelper } from "../../../../../../../react-core/Helpers/CommonHelper";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { PartLoading } from "../../../../../../../react-core/Html/PartLoading";
import { PercentageTextBoxEditor } from "../../../../../../../react-core/Html/PercentageTextBoxEditor";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";
import { InvestmentAllocation } from "../../../../../../../Wrap/Models/Domains/Investment/InvestmentAllocation";
import { User } from "../../../../../../../Wrap/Models/Models/User/User";
import { GENERIC_RESTRICTIONS_MESSAGE } from "../../../../../../Shared/Common/UserMessages";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { Table } from "../../../../../../Shared/Table/Table";
import { UserStore } from "../../../../../Stroes/Variable";
import { AssetsToSellFromPP } from "../../Store/PPToProductInstructionStatic";
import { RestrictionStore } from "../../../Common/Restrictions/Variable";

interface IChooseAssetsToSellTableProps extends IProcessComponentProps {
    data: InvestmentAllocation[];
    tableData: AssetsToSellFromPP[];
    totalValue: number;
}

interface IChooseAssetsToSellTableState {
    userInfo: User;
}

export class ChooseAssetsToSellTable extends ProcessComponent<
    IChooseAssetsToSellTableProps,
    IChooseAssetsToSellTableState
> {
    constructor(props: any) {
        super(props);
        this.state = {
            userInfo: UserStore.getUserInfo(),
        };
    }

    private onAssetsChanges(row: AssetsToSellFromPP, v: number) {
        const i = this.props.data.findIndex((ele) => ele.ProductId == row.ProductId);
        const _data = this.props.data.concat();
        if (i > -1) {
            _data[i].Percentage = v;
        } else {
            _data.push({ Name: row.AssetName, Percentage: v, ProductId: row.ProductId, Type: row.ProductType });
        }
        this.props.onChange(_data);
    }

    isValid() {
        const total = this.props.data?.reduce((sum, curr) => {
            return (sum = sum + curr.Percentage);
        }, 0);
        if (total !== 100) {
            this.props.showErrorMsgPopUp(["Please enter values against the assets to disinvest that total 100%"]);
        }
        return super.isValid() && total == 100;
    }

    

    public render(): JSX.Element {
        const { data, tableData, totalValue} = this.props;
        const sellAllocations = this.props.data;
        const total = data?.reduce((sum, curr) => {
            return (sum = sum + curr.Percentage);
        }, 0);

        if (isNullOrEmpty(tableData)) {
            return <PartLoading isLoading={true} />;
        }
        let _tableData = tableData.concat();

        if (RestrictionStore.sellDisabled()) {
            _tableData = [];
        }

        let restrictedMessage = "An account with suspended or restricted activities cannot setup sell investments";

        if (this.state.userInfo != null && this.state.userInfo.IsAdviser) {
            restrictedMessage = GENERIC_RESTRICTIONS_MESSAGE;
        }

        return (
            <>
                {RestrictionStore.sellDisabled() && <Alert alertType={AlertType.Error} message={restrictedMessage} />}
                <Table
                    data={_tableData}
                    col={[
                        {
                            title: "Assets",
                            key: "AssetName",
                            render: (data, row: AssetsToSellFromPP, index) => {
                                return (
                                    <a
                                        className={CommonHelper.isNullOrEmpty(row.FactsheetUrl) ? "" : "hyper-link"}
                                        href={row.FactsheetUrl}
                                        target="_blank"
                                    >
                                        {data}
                                    </a>
                                );
                            },
                        },
                        {
                            title: "Current estimated value",
                            key: "MarketValue",
                            render: (data, row, index) => {
                                return MoneyDisplayHelper.getMoneyFormat(data);
                            },
                        },
                        {
                            title: "Percentage of disinvestment amount",
                            thCssWrapper: "cell-right cell-15 pr-0",
                            key: "",
                            render: (data, row: AssetsToSellFromPP, index) => {
                                const percentage =
                                    sellAllocations.find((ele) => ele.ProductId == row.ProductId)?.Percentage || null;
                                return (
                                    <PercentageTextBoxEditor
                                        value={parseFloat((percentage || 0).toFixed(2))}
                                        onChange={(v) => this.onAssetsChanges(row, v)}
                                        maxNumberOfDecimalPlaces={{
                                            errorMessage: "only allow two decimal places",
                                            value: 2,
                                        }}
                                        disabled={row.DisinvestAll}
                                        validationRules={[
                                            {
                                                errorMessage:
                                                    "Please enter a value to disinvest that is less than 100% and equates to less than 100% of the current estimated value of the fund, or select to disinvest all units from the asset.",
                                                validator: (v) => {
                                                    return v <= 100;
                                                },
                                            },
                                            {
                                                errorMessage:
                                                    "Product allocation value exceeds 95% of total holding value",
                                                validator: (v) => {
                                                    let amount = (v || 0) * totalValue * 0.01;
                                                    return amount < row.MarketValue?.Amount * 0.95 || row.DisinvestAll;
                                                },
                                            },
                                            {
                                                errorMessage:
                                                    "You have entered a value to disinvest that is greater than the current estimated value of the asset.",
                                                validator: (v) => {
                                                    let amount = (v || 0) * totalValue * 0.01;
                                                    amount = Number(amount.toFixed(2));
                                                    return amount <= row.MarketValue?.Amount;
                                                },
                                            },
                                            {
                                                errorMessage:
                                                    "You have entered a value that is equal to the current estimated value of the asset. You may either choose to 'disinvest all' or reduce the value that you wish to disinvest.",
                                                validator: (v) => {
                                                    let amount = (v || 0) * totalValue * 0.01;
                                                    amount = Number(amount.toFixed(2));
                                                    return (
                                                        amount !== row.MarketValue?.Amount ||
                                                        (amount == row.MarketValue?.Amount && row.DisinvestAll)
                                                    );
                                                },
                                            },
                                            {
                                                errorMessage:
                                                    "Please enter an amount less than the proportional holding value of this fund.",
                                                validator: (v) => {
                                                    if(row.IsDeferred){
                                                       return (v || 0) <= (percentage || 0)
                                                    }
                                                    
                                                    return true;
                                                },
                                            },
                                        ]}
                                        infoText={MoneyDisplayHelper.getMoneyFormat({Amount: (percentage || 0) * totalValue * 0.01})
                                        }
                                    />
                                );
                            },
                        },
                        {
                            thCssWrapper: "cell-right pr-0",
                            tdCssWrapper: "cell-right pr-0",
                            title: "Disinvest all",
                            key: "DisinvestAll",
                            render: (data, row: AssetsToSellFromPP, index) => {
                                const percentage =
                                    sellAllocations.find((ele) => ele.ProductId == row.ProductId)?.Percentage || 0;
                                return (
                                    <CheckboxEditor
                                        cssWrapper={"square-checkbox mb-0"}
                                        onChange={(v) => {
                                            this.onAssetsChanges(row, v ? 100 : percentage);

                                            tableData[index].DisinvestAll = v;
                                        }}
                                        value={data}
                                    />
                                );
                            },
                        }
                    ]}
                    TotalBody={[
                        { data: "Total", colSpan: 2 },
                        {
                            data: (
                                <div style={{ textAlign: 'right' }}>
                                    <span className={total == 100 ? "text-positive" : "text-text"}>{total}%</span>
                                </div>
                            ),
                            colSpan: 1,
                        },
                        { data: "", colSpan: 1 }
                    ]}
                />
            </>
        );
    }
}
